import React from 'react';
import {
    Create,
    SimpleForm,
    useTranslate,
    useRedirect,
    useNotify,
    Toolbar,
    SaveButton,
} from 'react-admin';
import {
    Box,
    Typography,
    IconButton,
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { PolicyForm } from './PolicyForm';

const CreateToolbar = () => (
    <Toolbar sx={{ backgroundColor: '#FAFAFA', borderTop: '1px solid #E0E0E0', px: { xs: 2, sm: 3 }, py: 2, mt: { xs: 2, sm: 4 } }}>
        <SaveButton variant="contained" />
    </Toolbar>
);

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1.5, sm: 2 }, mb: { xs: 2, sm: 4 } }}>
            <IconButton
                onClick={() => redirect('/reservation-policies')}
                size="large"
                sx={{ backgroundColor: '#FFFFFF', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}
                title={translate('resources.reservation-policies.create.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.reservation-policies.create.title')}
                </Typography>
                <Typography sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }} color="text.secondary">
                    {translate('resources.reservation-policies.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

// next_slot mode bypasses lead-time / advance-days / cancel-window /
// modify-window / max-modifications semantics; zero them at submit so the
// DB doesn't carry stale values that the user can't see in the disabled UI.
// slotDurationMinutes goes to null since the hold window is used instead.
const transform = (data: any) => {
    // UI-level guard: fixed-slot policies are reservation-only. The form hides
    // the FCFS option for fixed_slot, but force it on submit too so a stale
    // form value can't slip through. Backend still permits the combo, so this
    // can be relaxed (here) in the future.
    if (data.reservationMode === 'fixed_slot' && data.accessMode === 'reserve_or_fcfs') {
        data = { ...data, accessMode: 'reservation_only' };
    }
    if (data.reservationMode !== 'next_slot') return data;
    return {
        ...data,
        slotDurationMinutes: null,
        leadTimeMinutes: 0,
        cancelWindowMinutes: 0,
        modifyWindowMinutes: 0,
        maxAdvanceDays: 1,
        maxModifications: 0,
    };
};

export const PolicyCreate = () => {
    const notify = useNotify();
    const redirect = useRedirect();

    return (
        <Create
            title={false}
            component="div"
            redirect="list"
            mutationMode="pessimistic"
            transform={transform}
            mutationOptions={{
                onSuccess: () => {
                    notify('resources.reservation-policies.notifications.created', { type: 'success' });
                    redirect('/reservation-policies');
                },
                onError: () => {
                    notify('resources.reservation-policies.notifications.error.create', { type: 'error' });
                },
            }}
        >
            <SimpleForm toolbar={<CreateToolbar />}>
                <Box sx={{ width: '100%' }}>
                    <PageHeader />
                    <PolicyForm mode="create" />
                </Box>
            </SimpleForm>
        </Create>
    );
};

export default PolicyCreate;
